import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import AddSectionDialog from './AddSectionDialog';
import VisualEditor from './visual/VisualEditor';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ListIcon from '@mui/icons-material/List';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { EmptyState } from 'dashboard/components/common';
import PageSettingsDrawer from './PageSettingsDrawer';
import PublishGuardDialog from './PublishGuardDialog';
import { REGIONS, slugify } from './utils';
import PageEditorToolbar from './PageEditorToolbar';
import PagePreviewPanel  from './PagePreviewPanel';
import PageRegionList    from './PageRegionList';
import usePendingEdits, { draftId } from './usePendingEdits';

/**
 * Owner-side page editor.
 *
 * Owns: the page object, drag state, all API calls (save / add /
 * delete / reorder / publish / settings). UI sub-pieces — toolbar,
 * preview, region lists, draggable section card — each live in their
 * own files. SectionCard / ComponentEditor / PageSettingsDrawer stay
 * as they were; they're focused already.
 */
export default function PageEditor({ pageId, brandId, catalog, onBack, onChange }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The canvas renders the real page, and the renderer substitutes the brand
  // name into template copy. Without it the editor would show "{{brand_name}}"
  // where the live page shows the brand.
  const [brand, setBrand] = useState(null);
  const [drag, setDrag] = useState({ region: null, draggedId: null, overId: null });
  // Sections that are still hidden when Publish is pressed, held while the
  // owner decides what to do about them.
  const [pendingPublish, setPendingPublish] = useState(null);
  // The section just added, so the editor can open straight onto it instead
  // of leaving someone to find the new empty band on the canvas.
  const [justAdded, setJustAdded] = useState(null);


  const reload = useCallback(() => {
    setLoading(true);
    return API.getSitePage({ brand_id: brandId, page_id: pageId })
      .then((res) => setPage(res))
      .catch((err) => toast.error(err?.message || 'Failed to load page'))
      .finally(() => setLoading(false));
  }, [brandId, pageId]);

  useEffect(() => { reload(); }, [reload]);

  // The page is held here while it is being worked on, and sent whole when
  // Save is pressed; see the hook for why.
  const {
    dirty, saving: savingDraft, edit, save, discard,
  } = usePendingEdits({ brandId, pageId, page, setPage, reload });

  const saveDraft = () => save().then(() => { onChange && onChange(); }).catch(() => {});

  const discardChanges = () => {
    if (!window.confirm('Throw away the changes you have made since the last save?')) return;
    discard().then(() => toast.info('Back to the last saved version'));
  };

  /**
   * Save first, then do something that replaces the whole page object.
   *
   * Page settings and publishing both answer with the page as the server has
   * it, which would overwrite the working copy — so anything unsaved goes
   * first. Publishing a page and finding the morning's changes missing from
   * it would be the worst version of this whole problem.
   */
  const withSaved = (fn) => (dirty ? save() : Promise.resolve()).then(fn);

  /** Everything in one region, in order, as the working copy has it. */
  const inRegion = (sections, region) => sections
    .filter((x) => (REGIONS.includes(x.region) ? x.region : 'main') === region);

  useEffect(() => {
    if (!brandId) return;
    API.getBrandById?.({ brand_id: brandId })
      .then(setBrand)
      .catch(() => setBrand(null)); // canvas still renders, just unsubstituted
  }, [brandId]);

  // Group sections by region and sort each group by `order` so the
  // displayed order matches the persisted one.
  const sectionsByRegion = useMemo(() => {
    const groups = { header: [], main: [], footer: [] };
    (page?.sectionData || []).forEach((s) => {
      const r = REGIONS.includes(s.region) ? s.region : 'main';
      groups[r].push(s);
    });
    Object.values(groups).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return groups;
  }, [page]);

  // Renaming answers with the page as the server has it, which would
  // overwrite the working copy — so anything unsaved goes first.
  const updateTitle = (next) => {
    setSavingTitle(true);
    withSaved(() => API.updateSitePage({
      brand_id: brandId, page_id: pageId,
      patch: { title: next, slug: slugify(next) },
    })
      .then((updated) => { setPage(updated); onChange && onChange(); })
      .catch((err) => toast.error(err?.message || 'Failed to save')))
      .catch(() => {})
      .finally(() => setSavingTitle(false));
  };

  // Adding a section opens a chooser rather than dropping a blank block on
  // the page. Without it the content library was invisible from the one place
  // it matters: a brand who had filled in their team had no way to put the
  // team on a page.
  const [addFor, setAddFor] = useState(null);
  // Design is the default: it is the one that shows people what they are
  // building. List stays for bulk work — reordering eight sections, checking
  // every colour at once — which a canvas is genuinely worse at.
  const [mode, setMode] = useState('design');
  // Full screen: the canvas is a page, and the dashboard's sidebar, navbar
  // and card padding steal roughly a third of the width it should have.
  const [fullscreen, setFullscreen] = useState(false);

  // Escape is what people press to get out of anything full screen.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const createSection = (spec) => {
    const id = draftId('section');
    const region = spec?.region || 'main';
    edit((prev) => ({
      ...prev,
      sectionData: [...(prev.sectionData || []), {
        _id: id,
        region,
        type: spec?.type || 'content',
        version: 'v1',
        name: spec?.name || `${spec?.type || 'content'} section`,
        layout: spec?.layout || 'contained',
        backgroundColor: 'transparent',
        textColor: '',
        // Hidden to begin with. Adding a section is the start of building it,
        // not the end, and the seconds between "add a pricing table" and
        // "fill in the prices" should not be seconds a visitor sees it.
        isVisible: false,
        componentData: [],
        ...spec,
      }],
    }));
    setJustAdded(id);
    toast.info('Added, and hidden for now — switch it on when it is ready.');
  };

  /**
   * Add a ready-made section.
   *
   * The server builds it and sends it back as plain data without writing
   * anything, because this editor does not write until Save is pressed. The
   * preset's blocks arrive with ids of our own making, exactly as if they had
   * been added by hand.
   */
  const createSectionFromPreset = (preset) =>
    API.buildSectionPreset({
      preset_key: preset.key,
      region: preset.region,
      brand_name: brand?.name || '',
    })
      .then((built) => {
        const id = draftId('section');
        edit((prev) => ({
          ...prev,
          sectionData: [...(prev.sectionData || []), {
            _id: id,
            region: built.region || preset.region || 'main',
            type: built.type,
            version: 'v1',
            name: built.name,
            layout: 'contained',
            backgroundColor: built.backgroundColor || 'transparent',
            textColor: built.textColor || '',
            isVisible: built.isVisible !== undefined ? built.isVisible : false,
            componentData: (built.components || []).map((c) => ({
              _id: draftId('block'),
              type: c.type,
              version: 'v1',
              name: `${c.type} component`,
              parent_id: null,
              content: c.content || {},
              styles: c.styles || {},
              settings: {},
            })),
          }],
        }));
        setJustAdded(id);
        toast.info('Added, and hidden for now — edit the words, then switch it on.');
      })
      .catch((err) => toast.error(err?.message || 'Failed to add section'));

  const patchSection = (section_id, patch) => edit((prev) => ({
    ...prev,
    sectionData: prev.sectionData.map((x) => (x._id === section_id ? { ...x, ...patch } : x)),
  }));

  const removeSection = (section_id) => {
    if (!window.confirm('Remove this section?')) return;
    edit((prev) => ({
      ...prev,
      sectionData: prev.sectionData.filter((x) => x._id !== section_id),
    }));
  };

  /**
   * Put a region's sections in the given order.
   *
   * The working copy is one flat list and the server takes the order from the
   * array it receives, so reordering means rebuilding that list with this
   * region's sections in their new places and every other region untouched.
   */
  const reorderRegion = (region, orderedIds) => edit((prev) => {
    const rank = new Map(orderedIds.map((id, i) => [String(id), i]));
    const moved = inRegion(prev.sectionData || [], region)
      .slice()
      .sort((a, b) => (rank.get(String(a._id)) ?? 0) - (rank.get(String(b._id)) ?? 0));
    let next = 0;
    return {
      ...prev,
      sectionData: (prev.sectionData || []).map((x) =>
        ((REGIONS.includes(x.region) ? x.region : 'main') === region ? moved[next++] : x)),
    };
  });

  /** The blocks of one section, mapped. */
  const inSection = (prev, section_id, fn) => ({
    ...prev,
    sectionData: prev.sectionData.map((x) =>
      (x._id !== section_id ? x : { ...x, componentData: fn(x.componentData || []) })),
  });

  const handleComponentChange = ({
    action, section_id, component_id, parent_id, type, patch, orderedIds,
  }) => {
    if (action === 'add') {
      const blockType = type || 'text';
      edit((prev) => inSection(prev, section_id, (blocks) => [...blocks, {
        _id: draftId('block'),
        type: blockType,
        version: 'v1',
        name: `${blockType} component`,
        parent_id: parent_id || null,
        content: {},
        settings: {},
        styles: {},
      }]));
      return;
    }
    // Both spellings, because the two editors grew apart: the list view sent
    // 'patch' and the design view sent 'update', and only 'patch' was handled
    // — so every edit made on the canvas was accepted by the UI, shown
    // optimistically, and never sent anywhere. Accepting both here means
    // neither caller can be wrong.
    if (action === 'patch' || action === 'update') {
      edit((prev) => ({
        ...prev,
        sectionData: prev.sectionData.map((x) => ({
          ...x,
          componentData: (x.componentData || []).map((c) =>
            (c._id === component_id ? { ...c, ...patch } : c)),
        })),
      }));
      return;
    }
    if (action === 'reorder') {
      // The order is the position in the list, so reordering rebuilds the
      // list rather than renumbering an `order` field beside it.
      edit((prev) => inSection(prev, section_id, (blocks) => {
        const rank = new Map((orderedIds || []).map((id, i) => [String(id), i]));
        const inside = new Set(orderedIds.map(String));
        const moved = blocks
          .filter((c) => inside.has(String(c._id)))
          .sort((a, b) => (rank.get(String(a._id)) ?? 0) - (rank.get(String(b._id)) ?? 0));
        let next = 0;
        return blocks.map((c) => (inside.has(String(c._id)) ? moved[next++] : c));
      }));
      return;
    }
    if (action === 'delete') {
      if (!window.confirm('Remove this block and anything inside it?')) return;
      // Deleting a grid deletes what is in it. Orphans pointing at a parent
      // that no longer exists render nowhere and cannot be reached to remove.
      edit((prev) => inSection(prev, section_id, (blocks) => {
        const doomed = new Set([String(component_id)]);
        // Walk down the tree until a pass adds nothing: a grid holds cards
        // and a card holds its own blocks, so one pass is not enough.
        for (let before = 0; before !== doomed.size;) {
          before = doomed.size;
          blocks.forEach((c) => {
            if (doomed.has(String(c.parent_id || ''))) doomed.add(String(c._id));
          });
        }
        return blocks.filter((c) => !doomed.has(String(c._id)));
      }));
    }
  };

  const saveSettings = (settings) => {
    setSavingSettings(true);
    withSaved(() => API.updateSitePage({ brand_id: brandId, page_id: pageId, patch: settings })
      .then((updated) => {
        setPage(updated); toast.success('Settings saved');
        setSettingsOpen(false);
        onChange && onChange();
      })
      .catch((err) => toast.error(err?.message || 'Failed to save')))
      .catch(() => {})
      .finally(() => setSavingSettings(false));
  };

  const publishOnly = (status) =>
    API.updateSitePage({ brand_id: brandId, page_id: pageId, patch: { status } })
      .then((updated) => { setPage(updated); toast.success(`Saved as ${status}`); onChange && onChange(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'));

  const applyPublish = (status) => withSaved(() => publishOnly(status))
    // A failed save already said so, and must not also publish.
    .catch(() => {});

  /**
   * Publishing asks about hidden sections first.
   *
   * Sections arrive hidden now, which is the right default while something is
   * being built — but it means the gap between "I finished this page" and
   * "the page is live" can be a section nobody remembered to switch on.
   * Publishing to find half your work missing is a bad way to learn that.
   */
  const updatePublish = (status) => {
    if (status !== 'published') { applyPublish(status); return; }
    const hidden = (page?.sectionData || []).filter((s) => s.isVisible === false);
    if (hidden.length === 0) { applyPublish('published'); return; }
    setPendingPublish(hidden);
  };

  if (loading && !page) {
    return (
      <Box sx={{ bgcolor: 'background.paper', borderRadius: '20px', p: '28px' }}>
        <EmptyState>Loading page…</EmptyState>
      </Box>
    );
  }
  if (!page) return null;

  const viewControls = (
    <>
      <ToggleButtonGroup
        size='small'
        exclusive
        value={mode}
        onChange={(_e, v) => v && setMode(v)}
      >
        <ToggleButton value='design'><ViewQuiltIcon fontSize='small' sx={{ mr: '6px' }} />Design</ToggleButton>
        <ToggleButton value='list'><ListIcon fontSize='small' sx={{ mr: '6px' }} />List</ToggleButton>
      </ToggleButtonGroup>
      {mode === 'design' && (
        <Button
          size='small'
          startIcon={<FullscreenIcon />}
          onClick={() => setFullscreen(true)}
          sx={{ ml: '8px' }}
        >
          Full screen
        </Button>
      )}
    </>
  );

  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: '20px', p: { xs: '20px', md: '28px' } }}>
      <PageEditorToolbar
        page={page}
        savingTitle={savingTitle}
        dirty={dirty}
        savingDraft={savingDraft}
        onSaveDraft={saveDraft}
        onDiscard={discardChanges}
        showPreview={showPreview}
        onBack={onBack}
        onTitleChange={updateTitle}
        onTogglePreview={() => setShowPreview((v) => !v)}
        onOpenSettings={() => setSettingsOpen(true)}
        onSaveStatus={updatePublish}
      />

      {showPreview && <PagePreviewPanel page={page} />}

      {/* In design mode these ride along in the editor's own control row, so
          there is one row of controls rather than two and the canvas lines up
          with the settings panel beside it. The list view has no such row, so
          there they keep one of their own. */}
      {mode === 'list' && (
        <Stack direction='row' justifyContent='flex-end' mb='12px'>
          {viewControls}
        </Stack>
      )}

      {mode === 'design' && !fullscreen && (
        <VisualEditor
          toolbar={viewControls}
          page={page}
          brand={brand}
          catalog={catalog}
          brandId={brandId}
          onPatchSection={patchSection}
          onRemoveSection={removeSection}
          onReorderRegion={reorderRegion}
          onComponentChange={(action, payload) => handleComponentChange({ action, ...payload })}
          onAddSectionAt={(region) => setAddFor(region)}
          focusSectionId={justAdded}
          onFocused={() => setJustAdded(null)}
        />
      )}

      {mode === 'list' && REGIONS.map((region) => (
        <PageRegionList
          key={region}
          region={region}
          sections={sectionsByRegion[region]}
          drag={drag}
          setDrag={setDrag}
          catalog={catalog}
          brandId={brandId}
          page={page}
          onAddSection={(region) => setAddFor(region)}
          onPatchSection={patchSection}
          onRemoveSection={removeSection}
          onComponentChange={handleComponentChange}
          onReorderRegion={reorderRegion}
        />
      ))}

      <PageSettingsDrawer
        brandId={brandId}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        page={page}
        onSave={saveSettings}
        isSubmitting={savingSettings}
      />

      {/* A plain fixed overlay rather than the Fullscreen API: that hands the
          page to the browser's own chrome, which swallows Escape and hides
          the toast notifications the editor relies on. This keeps the app in
          charge and still gives the canvas the whole viewport. */}
      {fullscreen && mode === 'design' && (
        <Box
          sx={{
            position: 'fixed', inset: 0, zIndex: 1300,
            bgcolor: 'background.default',
            overflowY: 'auto',
            p: { xs: '12px', md: '16px' },
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap='10px'
            flexWrap='wrap'
            mb='12px'
          >
            <Typography variant='body2' fontWeight='700'>
              {page?.title} <Box component='span' sx={{ color: 'text.secondary', fontWeight: 400 }}>/{page?.slug}</Box>
            </Typography>
            <Stack direction='row' spacing='8px' flexWrap='wrap'>
              <Button size='small' onClick={() => setSettingsOpen(true)}>Settings</Button>
              <Button size='small' variant='contained' onClick={() => updatePublish('published')}>
                Publish
              </Button>
              <Button
                size='small'
                startIcon={<FullscreenExitIcon />}
                onClick={() => setFullscreen(false)}
              >
                Exit
              </Button>
            </Stack>
          </Stack>

          <VisualEditor
            page={page}
            brand={brand}
            catalog={catalog}
            brandId={brandId}
            onPatchSection={patchSection}
            onRemoveSection={removeSection}
            onReorderRegion={reorderRegion}
            onComponentChange={(action, payload) => handleComponentChange({ action, ...payload })}
            onAddSectionAt={(region) => setAddFor(region)}
            focusSectionId={justAdded}
            onFocused={() => setJustAdded(null)}
          />
        </Box>
      )}

      <PublishGuardDialog
        open={Boolean(pendingPublish)}
        sections={pendingPublish || []}
        onCancel={() => setPendingPublish(null)}
        onConfirm={(ids) => {
          setPendingPublish(null);
          // Switch the chosen sections on first, then publish — publishing a
          // page and then revealing its sections would put it live in its
          // incomplete state for as long as the second call takes.
          //
          // The next page is built here and handed to the save directly: it
          // was set a moment ago in this same handler, and reading it back
          // from state would give the version before the change.
          const wanted = new Set(ids.map(String));
          const next = {
            ...page,
            sectionData: (page.sectionData || []).map((x) =>
              (wanted.has(String(x._id)) ? { ...x, isVisible: true } : x)),
          };
          setPage(next);
          save(next)
            .then(() => publishOnly('published'))
            .catch(() => {});
        }}
      />

      <AddSectionDialog
        open={Boolean(addFor)}
        region={addFor || 'main'}
        brandId={brandId}
        onClose={() => setAddFor(null)}
        onAdd={createSection}
        onAddPreset={createSectionFromPreset}
      />
    </Box>
  );
}
